if (!customElements.get('theme-newsletter-form')) {
  const KLAVIYO_SUBSCRIPTIONS_URL = 'https://a.klaviyo.com/client/subscriptions';
  const KLAVIYO_REVISION = '2024-10-15';

  class ThemeNewsletterForm extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this._controller?.abort();
      this._controller = new AbortController();
      const { signal } = this._controller;

      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.submitButton = this.querySelector('[data-newsletter-submit]');
      this.spinner = this.querySelector('.loading__spinner');
      this.statusEl = this.querySelector('[data-newsletter-status]');
      this.thanksEl = this.querySelector('[data-newsletter-thanks]');

      this.integration = this.dataset.integration || 'shopify';
      this.source = this.dataset.source || 'section';
      this.klaviyoPublicKey = this.dataset.klaviyoPublicKey || '';
      this.klaviyoList = this.dataset.klaviyoList || '';
      this.mailchimpUrl = this.dataset.mailchimpUrl || '';
      this.doubleOptIn = this.dataset.doubleOptIn === 'true';
      this.postSignupAction = this.dataset.postSignup || 'thanks_message';
      this.redirectUrl = this.dataset.redirectUrl || '';

      this.ensureFieldError();
      this.form?.addEventListener('submit', this.handleSubmit, { signal });
      window.addEventListener('pageshow', (event) => event.persisted && this.setLoading(false), { signal });

      if (this.integration === 'shopify') this.handleServerResult();
    }

    handleServerResult() {
      if (this.querySelector('[data-newsletter-success]')) {
        if (this._successHandled) return;
        this._successHandled = true;
        this.handleSuccess({ showMessage: false });
        return;
      }

      const serverError = this.fieldErrorEl?.hidden === false && this.fieldErrorEl.textContent.trim();
      if (serverError) {
        theme.pubsub?.publish(theme.pubsub.events.FORM_ERROR, { integration: this.integration, error: serverError });
      }
    }

    disconnectedCallback() {
      this._controller?.abort();
    }

    ensureFieldError() {
      const wrapper = this.emailInput?.closest('.form-field');
      if (!wrapper || !this.emailInput?.id) return;

      const errorId = `${this.emailInput.id}-error`;
      let errorEl = wrapper.querySelector(`#${errorId}`);

      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.id = errorId;
        errorEl.className = 'form-field__error';
        errorEl.setAttribute('role', 'alert');
        errorEl.hidden = true;
        wrapper.appendChild(errorEl);
      }

      const describedby = this.emailInput.getAttribute('aria-describedby') || '';
      if (!describedby.split(' ').includes(errorId)) {
        this.emailInput.setAttribute('aria-describedby', describedby ? `${describedby} ${errorId}` : errorId);
      }

      this.fieldErrorEl = errorEl;
      this.fieldWrapper = wrapper;
    }

    async handleSubmit(event) {
      const isNative = this.integration === 'shopify';
      if (isNative && this.isLoading) return;

      if (!isNative) event.preventDefault();
      if (this.isLoading) return;

      const email = this.emailInput?.value.trim();
      if (!this.isValidEmail(email)) {
        event.preventDefault();
        this.handleError(theme.locales.newsletter.error, { silent: true });
        return;
      }

      this.clearFieldError();
      this.setLoading(true);
      theme.pubsub?.publish(theme.pubsub.events.FORM_SUBMIT, { integration: this.integration });

      if (isNative) return;

      try {
        if (this.integration === 'klaviyo') {
          await this.submitKlaviyo(email);
        } else {
          await this.submitMailchimp(email);
        }
      } finally {
        this.setLoading(false);
      }
    }

    isValidEmail(email) {
      return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    async submitKlaviyo(email) {
      if (!this.klaviyoPublicKey || !this.klaviyoList) {
        this.handleError(theme.locales.newsletter.error);
        return;
      }

      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.source,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: this.klaviyoList } } },
        },
      };

      try {
        const response = await fetch(`${KLAVIYO_SUBSCRIPTIONS_URL}?company_id=${encodeURIComponent(this.klaviyoPublicKey)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            accept: 'application/vnd.api+json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          this.handleSuccess();
          return;
        }

        this.handleError(theme.locales.newsletter.error);
      } catch (error) {
        this.handleError(theme.locales.form.error.network);
      }
    }

    async submitMailchimp(email) {
      if (!this.mailchimpUrl) {
        this.handleError(theme.locales.newsletter.error);
        return;
      }

      const formData = new FormData();
      formData.append('EMAIL', email);

      try {
        // Classic Mailchimp embedded forms don't support CORS: the response is opaque
        // under mode "no-cors" and cannot be inspected. A completed request is the
        // only signal available, so it's treated as an optimistic success.
        await fetch(this.mailchimpUrl, {
          method: 'POST',
          mode: 'no-cors',
          body: formData,
        });
        this.handleSuccess();
      } catch (error) {
        this.handleError(theme.locales.form.error.network);
      }
    }

    handleSuccess({ showMessage = true } = {}) {
      const message = this.decodeEntities(
        this.doubleOptIn ? theme.locales.newsletter.doubleOptInMessage : theme.locales.newsletter.success
      );

      theme.toast?.show({ type: 'success', message });
      this.announce(message);
      theme.pubsub?.publish(theme.pubsub.events.FORM_SUCCESS, { integration: this.integration });

      if (this.postSignupAction === 'redirect' && this.redirectUrl) {
        window.location.href = this.redirectUrl;
        return;
      }

      if (showMessage) this.showThanks(message);
    }

    handleError(message, { silent = false } = {}) {
      message = this.decodeEntities(message);
      if (!silent) {
        theme.toast?.show({ type: 'error', message });
        theme.pubsub?.publish(theme.pubsub.events.FORM_ERROR, { integration: this.integration, error: message });
      }

      this.announce(message);
      this.showFieldError(message);
    }

    showThanks(message) {
      if (!this.form || !this.thanksEl) return;
      this.form.hidden = true;
      this.thanksEl.textContent = message;
      this.thanksEl.hidden = false;
    }

    showFieldError(message) {
      if (this.fieldErrorEl) {
        this.fieldErrorEl.textContent = message;
        this.fieldErrorEl.hidden = false;
      }
      this.fieldWrapper?.classList.add('form-field--error');
      this.emailInput?.setAttribute('aria-invalid', 'true');
    }

    clearFieldError() {
      if (this.fieldErrorEl) {
        this.fieldErrorEl.hidden = true;
        this.fieldErrorEl.textContent = '';
      }
      this.fieldWrapper?.classList.remove('form-field--error');
      this.emailInput?.removeAttribute('aria-invalid');
    }

    announce(message) {
      if (this.statusEl) this.statusEl.textContent = message;
    }

    decodeEntities(str) {
      if (!str || str.indexOf('&') === -1) return str;
      const el = document.createElement('textarea');
      el.innerHTML = str;
      return el.value;
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.submitButton?.toggleAttribute('disabled', isLoading);
      this.submitButton?.setAttribute('aria-busy', String(isLoading));
      this.spinner?.classList.toggle('hidden', !isLoading);
      if (this.emailInput && this.integration !== 'shopify') this.emailInput.disabled = isLoading;
    }
  }

  customElements.define('theme-newsletter-form', ThemeNewsletterForm);
}
